/**
 * 场景缓存数据库
 * 用于缓存每个角色对应的场景列表
 */

// 场景缓存数据接口
export interface SceneCache {
  roleId: number | string // 角色ID（主键）
  sceneList: any[] // 场景列表数据
  lastUpdated: number // 最后更新时间戳
}

/**
 * 缓存过期时间（默认 30 分钟）
 */
const CACHE_EXPIRY_TIME = 30 * 60 * 1000

class SceneCacheDB {
  private db: IDBDatabase | null = null
  private readonly dbName = 'RoleplaySceneCacheDB'
  private readonly version = 1

  /**
   * 初始化数据库
   */
  async init(): Promise<void> {
    if (this.db) {
      return
    }

    return new Promise<void>((resolve, reject) => {
      const req = indexedDB.open(this.dbName, this.version)

      req.onerror = () => {
        console.error('❌ SceneCacheDB 打开失败:', req.error)
        reject(req.error)
      }

      req.onsuccess = () => {
        this.db = req.result
        console.log('✅ SceneCacheDB 初始化成功')
        resolve()
      }

      req.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result

        // 场景缓存表
        if (!db.objectStoreNames.contains('sceneCache')) {
          const store = db.createObjectStore('sceneCache', { keyPath: 'roleId' })
          store.createIndex('lastUpdated', 'lastUpdated', { unique: false })
          console.log('📦 创建场景缓存表')
        }
      }
    })
  }

  /**
   * 获取事务
   */
  private getTransaction(mode: IDBTransactionMode): IDBTransaction {
    if (!this.db) throw new Error('SceneCacheDB 未初始化，请先调用 init()')
    return this.db.transaction(['sceneCache'], mode)
  }

  /**
   * Promise 化 IDBRequest
   */
  private promisify<T>(req: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      req.onsuccess = () => resolve(req.result as T)
      req.onerror = () => reject(req.error)
    })
  }

  /**
   * 保存场景列表到缓存
   */
  async saveSceneCache(roleId: number | string, sceneList: any[]): Promise<void> {
    try {
      await this.init()
      const tx = this.getTransaction('readwrite')
      const cache: SceneCache = {
        roleId,
        sceneList,
        lastUpdated: Date.now()
      }
      await this.promisify(tx.objectStore('sceneCache').put(cache))
      console.log(`✅ 场景缓存已保存 - 角色ID: ${roleId}, 场景数量: ${sceneList.length}`)
    } catch (error) {
      console.error('❌ 保存场景缓存失败:', error)
    }
  }

  /**
   * 获取场景列表缓存
   * @param roleId 角色ID
   * @param maxAge 缓存最大有效期（毫秒），默认 30 分钟
   * @returns 场景列表或 null（如果缓存不存在或已过期）
   */
  async getSceneCache(roleId: number | string, maxAge: number = CACHE_EXPIRY_TIME): Promise<any[] | null> {
    try {
      await this.init()
      const tx = this.getTransaction('readonly')
      const cache = await this.promisify<SceneCache | undefined>(
        tx.objectStore('sceneCache').get(roleId)
      )

      if (!cache) {
        console.log(`📭 场景缓存不存在 - 角色ID: ${roleId}`)
        return null
      }

      const age = Date.now() - cache.lastUpdated
      if (age > maxAge) {
        console.log(`⏰ 场景缓存已过期 - 角色ID: ${roleId}, 过期时间: ${Math.round(age / 1000)}秒`)
        // 删除过期缓存
        await this.deleteSceneCache(roleId)
        return null
      }

      console.log(
        `✅ 场景缓存命中 - 角色ID: ${roleId}, 场景数量: ${cache.sceneList.length}, 缓存年龄: ${Math.round(age / 1000)}秒`
      )
      return cache.sceneList
    } catch (error) {
      console.error('❌ 获取场景缓存失败:', error)
      return null
    }
  }

  /**
   * 删除指定角色的场景缓存
   */
  async deleteSceneCache(roleId: number | string): Promise<void> {
    try {
      await this.init()
      const tx = this.getTransaction('readwrite')
      await this.promisify(tx.objectStore('sceneCache').delete(roleId))
      console.log(`🗑️ 场景缓存已删除 - 角色ID: ${roleId}`)
    } catch (error) {
      console.error('❌ 删除场景缓存失败:', error)
    }
  }

  /**
   * 清空所有场景缓存
   */
  async clearAllSceneCache(): Promise<void> {
    try {
      await this.init()
      const tx = this.getTransaction('readwrite')
      await this.promisify(tx.objectStore('sceneCache').clear())
      console.log('🗑️ 所有场景缓存已清空')
    } catch (error) {
      console.error('❌ 清空场景缓存失败:', error)
    }
  }

  /**
   * 清理所有过期的场景缓存
   */
  async cleanExpiredSceneCache(maxAge: number = CACHE_EXPIRY_TIME): Promise<void> {
    try {
      await this.init()
      const now = Date.now()
      const tx = this.getTransaction('readwrite')
      const store = tx.objectStore('sceneCache')
      const allCaches = await this.promisify<SceneCache[]>(store.getAll())

      const expiredRoleIds = allCaches
        .filter((cache) => now - cache.lastUpdated > maxAge)
        .map((cache) => cache.roleId)

      if (expiredRoleIds.length > 0) {
        await Promise.all(expiredRoleIds.map((roleId) => this.promisify(store.delete(roleId))))
        console.log(`🗑️ 已清理 ${expiredRoleIds.length} 个过期的场景缓存`)
      } else {
        console.log('✅ 没有过期的场景缓存需要清理')
      }
    } catch (error) {
      console.error('❌ 清理过期场景缓存失败:', error)
    }
  }

  /**
   * 获取缓存统计信息
   */
  async getSceneCacheStats(): Promise<{
    totalCount: number
    totalSize: number
    oldestCache: number | null
    newestCache: number | null
  }> {
    try {
      await this.init()
      const tx = this.getTransaction('readonly')
      const allCaches = await this.promisify<SceneCache[]>(tx.objectStore('sceneCache').getAll())

      const totalCount = allCaches.length
      const totalSize = allCaches.reduce((sum, cache) => sum + cache.sceneList.length, 0)
      const timestamps = allCaches.map((cache) => cache.lastUpdated)
      const oldestCache = timestamps.length > 0 ? Math.min(...timestamps) : null
      const newestCache = timestamps.length > 0 ? Math.max(...timestamps) : null

      return {
        totalCount,
        totalSize,
        oldestCache,
        newestCache
      }
    } catch (error) {
      console.error('❌ 获取缓存统计信息失败:', error)
      return {
        totalCount: 0,
        totalSize: 0,
        oldestCache: null,
        newestCache: null
      }
    }
  }
  /**
   * 删除所有场景缓存
   * */ 
  async deleteAll(): Promise<void> {
    try {
      await this.init()
      const tx = this.getTransaction('readwrite')
      await this.promisify(tx.objectStore('sceneCache').clear())
      console.log('🗑️ 所有场景缓存已清空')
    } catch (error) {
      console.error('❌ 删除所有场景缓存失败:', error)
    }
  }
}

// 创建单例实例
const sceneCacheDB = new SceneCacheDB()

// 导出便捷方法
export const saveSceneCache = (roleId: number | string, sceneList: any[]) =>
  sceneCacheDB.saveSceneCache(roleId, sceneList)

export const getSceneCache = (roleId: number | string, maxAge?: number) =>
  sceneCacheDB.getSceneCache(roleId, maxAge)

export const deleteSceneCache = (roleId: number | string) => sceneCacheDB.deleteSceneCache(roleId)

export const clearAllSceneCache = () => sceneCacheDB.clearAllSceneCache()

export const cleanExpiredSceneCache = (maxAge?: number) =>
  sceneCacheDB.cleanExpiredSceneCache(maxAge)

export const getSceneCacheStats = () => sceneCacheDB.getSceneCacheStats()

// 导出数据库实例（如果需要直接操作）
export { sceneCacheDB }
